import FirebaseAuthHelper from "../firebase-auth-helper/firebase-auth-helper";
import { UserStoreActions } from "../../../../redux/user-store";
import { store } from "../../../../redux/store";
import ApiCalls from "../../../../api";
import router from "../../../../shared/top-level-declarations/history";

const AuthHelper = {
  // Login method that handles Firebase authentication and token exchange with backend
  login: async (email, password) => {
    try {
      // Step 1: Dispatch login request action to update the loading state
      store.dispatch(UserStoreActions.loginRequest());

      // Step 2: Firebase login using email and password
      const firebaseUser = await FirebaseAuthHelper.login({ email, password });

      // Step 3: Get Firebase ID token to exchange for access and refresh tokens
      const firebaseToken = await firebaseUser.getIdToken();

      // Step 4: Send the Firebase token to your backend to get access and refresh tokens
      const response = await ApiCalls.user.public.login(firebaseToken);
      const { user, accessToken, refreshToken } = response.data.data; // Extract user, accessToken, and refreshToken
      console.log('response :>> ', response);
      // Step 5: Dispatch success action to store user info, accessToken, and refreshToken
      store.dispatch(
        UserStoreActions.loginSuccess({
          user, // Store user info
          accessToken, // Store access token
          refreshToken, // Store refresh token
        })
      );
      return user; // Return user data if needed
    } catch (error) {
      // Step 6: Dispatch failure action and pass the error message
      store.dispatch(UserStoreActions.loginFailure(error.message));
      throw error; // Re-throw the error for handling in the component
    }
  },

  // Login B2C with Google and exchange token with backend
  loginB2CWithGoogle: async (options = {}) => {
    try {
      store.dispatch(UserStoreActions.loginRequest());
      const firebaseUser = await FirebaseAuthHelper.loginWithGoogle();
      const firebaseToken = await firebaseUser.getIdToken();
      const response = await ApiCalls.user.public.loginB2C(firebaseToken);
      const { user, accessToken, refreshToken } = response.data.data || {};
      store.dispatch(
        UserStoreActions.loginSuccess({ user, accessToken, refreshToken })
      );
      if (!options?.skipNavigate) {
        router.navigate("/manage-groups");
      }
      return user;
    } catch (error) {
      store.dispatch(UserStoreActions.loginFailure(error.message));
      throw error;
    }
  },

  // Method to refresh the access token using the refresh token
  refreshToken: async () => {
    const state = store.getState();
    const token = state.userStore?.refreshToken;
    if (!token) {
      // fallback: try firebase token refresh flow
      const firebaseToken = await FirebaseAuthHelper.getFirebaseAuthToken();
      const response = await ApiCalls.user.public.login(firebaseToken);
      const { user, accessToken, refreshToken } = response.data.data || {};
      store.dispatch(
        UserStoreActions.loginSuccess({ user, accessToken, refreshToken })
      );
      return accessToken;
    }

    const response = await ApiCalls.user.public.refreshToken(token);
    const { accessToken, refreshToken } = response.data.data || {};
    store.dispatch(UserStoreActions.updateTokens({ accessToken, refreshToken }));
    return accessToken;
  },

  // Logout method that handles Firebase logout and Redux state update
  logout: async () => {
    try {
      await FirebaseAuthHelper.logout(); // Call Firebase logout
      store.dispatch(UserStoreActions.logout()); // Dispatch logout to store
      // router.navigate("/login"); // Navigate to login page
    } catch (error) {
      console.error("Error logging out:", error.message);
    }
  },

  // Clear error method for resetting error states in Redux
  clearError: () => {
    store.dispatch(UserStoreActions.clearError());
  },

  // Method to get the current user authentication state from Redux
  getAuthState: () => {
    const state = store.getState();
    return state.userStore; // Return the user store state
  },

  getAccessToken: () => {
    const state = store.getState();
    return state.userStore?.accessToken; // Return the access token
  },

  resetPassword: async (email) => {
    try {
      await FirebaseAuthHelper.resetPassword(email);
    } catch (error) {
      throw error;
    }
  },

  // Send signup link to email
  sendSignupLink: async ({ email }) => {
    try {
      const actionCodeSettings = {
        url: `${window.location.origin}/signup/complete`,
        handleCodeInApp: true,
      };

      await FirebaseAuthHelper.sendSignupLink({ email, actionCodeSettings });

      // Store email in localStorage for retrieval after redirect
      window.localStorage.setItem('emailForSignIn', email);
    } catch (error) {
      throw error;
    }
  },

  // Complete signup with email link
  completeSignupWithEmailLink: async ({ email }) => {
    try {
      const url = window.location.href;

      if (!FirebaseAuthHelper.isSignupLink(url)) {
        throw new Error('Invalid signup link');
      }

      const userCredential = await FirebaseAuthHelper.completeSignupWithLink({
        email,
        url
      });

      // Clear stored email
      window.localStorage.removeItem('emailForSignIn');

      return userCredential.user;
    } catch (error) {
      throw error;
    }
  },

  // Complete signup process (create user in backend)
  signup: async ({
    firebaseToken,
    name,
    email,
    password,
    phone,
    profileImgUrl,
    organization
  }) => {
    try {
      store.dispatch(UserStoreActions.loginRequest());

      const payload = {
        firebaseToken,
        name,
        email,
        organization,
        ...(password && { password }),
        ...(phone && { phone }),
        ...(profileImgUrl && { profileImgUrl }),
      };

      const response = await ApiCalls.user.public.signup(payload);

      const { user, accessToken, refreshToken } = response.data.data;

      // Store in Redux
      store.dispatch(UserStoreActions.loginSuccess({
        user,
        accessToken,
        refreshToken
      }));

      return response.data.data;
    } catch (error) {
      store.dispatch(UserStoreActions.loginFailure(error.message));
      throw error;
    }
  },

  // Check if email exists
  checkEmailExists: async (email) => {
    try {
      const response = await ApiCalls.user.public.checkEmail(email);
      return response.data.data.exists;
    } catch (error) {
      throw error;
    }
  },
};

export default AuthHelper;
